import _ from "underscore";
import DriveView from "../../DriveView";
import SelectList, { ISelectListOptions } from "./selectList";
import { Dialog } from "./dialog";
import Api from "../../services/api";
import Translator from "../../utils/translator";
import AppView from "../../app.view";
import { getServerDate } from "../../utils/storage/date";
import Spinner from "../../utils/spinner";
import { TMultiCallParam } from "../../utils/api.interfaces";
import { handleApi } from "../../utils/error";

import vehicleSelectDialogTemplate from "./vehicleSelectDialog.tmpl";

interface IVehicleSelectDialog extends Backbone.ViewOptions<Backbone.Model> {
    translator: Translator;
    app: AppView;
    title?: string;
    selectedVehicleId?: string;
    selectCallback: (...args) => void;
}

const SEARCH_START_DELAY = 500; // Delay before starting search after search string changed in ms
const WAITING_START_DELAY = 200; // Delay before showing search waiting
const WAITING_STOP_DELAY = 700; // Delay before hiding search waiting

export default class VehicleSelectDialog extends DriveView<Backbone.Model> {
    private readonly selectCallback;
    private readonly spinner;
    private readonly api: Api;
    private readonly title: string;
    private readonly selectedVehicleId?: string;
    private searchTimer: number;
    private dialog: Dialog | null;
    private waitingTimer: number;
    private searchString = "";

    constructor(options: IVehicleSelectDialog) {
        super(options);
        this.api = this.app.api;
        this.selectCallback = options.selectCallback;
        this.title = options.title || "Select Vehicle";
        this.selectedVehicleId = options.selectedVehicleId;
        this.spinner = new Spinner();
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "input .vehicle-select__input": this.onSearchFieldUpdate,
            "change .vehicle-select__input": this.searchVehicles,
            "click .vehicle-select__retry": this.searchVehicles,
            "click #driver-asset-selection-no-vehicle-no-last-asset": this.selectNoVehicle
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        let templateStr = (_.template(vehicleSelectDialogTemplate))({});

        this.dialog = new Dialog(
            "select-vehicle",
            `<div class="selectDialogContent">${ templateStr }</div>`,
            this.translator,
            this.title,
            undefined,
            {
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void this.dialog.open().then(() => {
            this.dialog?.el && this.setElement(this.dialog.el.querySelector(".selectDialogContent"));
        });
        return this;
    }

    showRetryForm() {
        this.el.querySelector(".vehicle-select__failed").style.display = "block";
    }

    hideRetryForm() {
        this.el.querySelector(".vehicle-select__failed").style.display = "none";
    }

    onSearchFieldUpdate() {
        this.searchTimer && clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.searchVehicles(), SEARCH_START_DELAY);
    }

    searchVehicles() {
        let searchKey = (<HTMLInputElement> this.el.querySelector(".vehicle-select__input")).value.trim();

        let searchCalls = [
            ["Get", {
                typeName: "Device",
                resultsLimit: 20,
                search: {
                    name: `%${ searchKey }%`,
                    fromDate: getServerDate().toISOString(),
                    groups: [{id: "GroupVehicleId" }]
                }
            }],
            ["Get", {
                typeName: "Device",
                resultsLimit: 20,
                search: {
                    vehicleIdentificationNumber: `%${ searchKey }%`,
                    fromDate: getServerDate().toISOString(),
                    groups: [{id: "GroupVehicleId" }]
                }
            }],
            ["Get", {
                typeName: "Device",
                resultsLimit: 20,
                search: {
                    licensePlate: `%${ searchKey }%`,
                    fromDate: getServerDate().toISOString(),
                    groups: [{id: "GroupVehicleId" }]
                }
            }],
            ["Get", {
                typeName: "Device",
                resultsLimit: 20,
                search: {
                    serialNumber: `%${ searchKey }%`,
                    fromDate: getServerDate().toISOString(),
                    groups: [{id: "GroupVehicleId" }]
                }
            }]
        ] as TMultiCallParam[];

        this.searchTimer && clearTimeout(this.searchTimer);

        if (searchKey === this.searchString) {
            return;
        }
        this.searchString = searchKey;

        this.setWaiting(true, () => {
            this.hideRetryForm();
            this.el.querySelector(".vehicle-select__vehicles-list").innerHTML = "";
            this.dialog && this.dialog.resize();
        });

        this.api.multiCall(searchCalls, results => {
            let devices = results.reduce((all, result) => all.concat(result), []);
            let hash = devices.reduce((res, device) => {
                res[device.id] = device;
                return res;
            }, {});
            let selectList;

            let devicesList: ISelectListOptions[] = Object.keys(hash)
                .map(id => {
                    let device = hash[id];
                    return {
                        id: device.id,
                        name: device.name,
                        isSelected: device.id === this.selectedVehicleId
                    };
                })
                .sort((a, b) => a.name - b.name);

            this.setWaiting(false, () => {
                if (devicesList.length > 0) {
                    this.translator.translate(this.title).then(title => {
                        selectList = new SelectList({
                            el: this.el.querySelector(".vehicle-select__vehicles-list"),
                            title,
                            selectCallback: device => {
                                this.onSelectVehicle(device);
                                selectList = null;
                            }
                        });
                        this.hideRetryForm();
                        selectList.render(devicesList);
                    }).catch(handleApi);
                } else {
                    this.el.querySelector(".vehicle-select__vehicles-list").innerHTML = "";
                    this.showRetryForm();
                }
                this.dialog && this.dialog.resize();
            });
        }, () => {
            this.setWaiting(false, () => {
                this.showRetryForm();
                this.dialog && this.dialog.resize();
            });
        });
    }

    selectNoVehicle() {
        this.onSelectVehicle({ id: "NoDeviceId" });
    }

    onSelectVehicle(device) {
        this.selectCallback && this.selectCallback(device);
        this.remove();
    }

    remove() {
        this.dialog && this.dialog.remove();

        super.remove();
        this.waitingTimer && clearTimeout(this.waitingTimer);
        this.searchTimer && clearTimeout(this.searchTimer);
        return this;
    }

    // Add delay before showing/hiding popup to prevent flickering
    private setWaiting(isShow: boolean, beforeShow: () => void) {
        this.waitingTimer && clearTimeout(this.waitingTimer);
        this.waitingTimer = setTimeout(() => {
            beforeShow();
            isShow ? this.showVehicleWaiting() : this.hideVehicleWaiting();
            this.dialog && this.dialog.resize();
        }, isShow ? WAITING_START_DELAY : WAITING_STOP_DELAY);
    }

    private showVehicleWaiting() {
        let spinnerContainer = this.el.querySelector(".vehicle-select__waiting");
        spinnerContainer.style.display = "block";
        this.spinner.spin(spinnerContainer);
    }

    private hideVehicleWaiting() {
        let spinnerContainer = this.el.querySelector(".vehicle-select__waiting");
        this.spinner.stop();
        spinnerContainer.style.display = "none";
    }
}